import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, X } from 'lucide-react';
import TableExportButton from './TableExportButton';

const ExpandableAIChatTable = ({ headerRow, dataRows, totalRows, headerCount, numericCols, tableMarkdown, hotelName, isDarkMode }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Lock body scroll when fullscreen is open
  useEffect(() => {
    if (isFullscreen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    };
  }, [isFullscreen]);

  // Close on Escape key
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Touch handling for overlay dismiss (distinguishes taps from scrolls)
  const touchStartRef = useRef(null);
  const handleOverlayTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const handleOverlayClick = (e) => {
    if (touchStartRef.current) {
      const threshold = 10;
      const touch = touchStartRef.current;
      const dx = Math.abs(e.clientX - touch.x);
      const dy = Math.abs(e.clientY - touch.y);
      if (dx > threshold || dy > threshold) {
        touchStartRef.current = null;
        return;
      }
    }
    touchStartRef.current = null;
    setIsFullscreen(false);
  };

  const renderRow = (row, rowIdx, rowType) => {
    const cells = row.split('|').filter(cell => cell.trim());
    const isHeader = rowType === 'header';
    const isTotalRow = rowType === 'total';

    if (isTotalRow && cells.length < headerCount) {
      const label = cells[0] || '';
      const values = cells.slice(1);
      const sortedNumCols = [...numericCols].sort((a, b) => a - b);
      const aligned = new Array(headerCount).fill('');
      aligned[0] = label;
      values.forEach((v, i) => {
        if (i < sortedNumCols.length) {
          aligned[sortedNumCols[i]] = v;
        }
      });
      cells.length = 0;
      cells.push(...aligned);
    }
    while (cells.length < headerCount) {
      cells.push('');
    }

    return (
      <tr key={`${rowType}-${rowIdx}`} className={isTotalRow ? 'total-row' : ''}>
        {cells.slice(0, headerCount).map((cell, cellIdx) => {
          const cellContent = cell.trim();
          const displayContent = cellContent.replace(/\*\*/g, '');
          const stickyClass = cellIdx === 0 ? 'ai-chat-sticky-col' : '';

          return isHeader ? (
            <th key={cellIdx} className={stickyClass}>{displayContent}</th>
          ) : (
            <td key={cellIdx} className={stickyClass}>{displayContent}</td>
          );
        })}
      </tr>
    );
  };

  const renderTable = () => (
    <table className="ai-chat-table">
      <thead>
        {renderRow(headerRow, 0, 'header')}
      </thead>
      <tbody>
        {dataRows.map((row, idx) => renderRow(row, idx, 'data'))}
      </tbody>
      {totalRows.length > 0 && (
        <tfoot>
          {totalRows.map((row, idx) => renderRow(row, idx, 'total'))}
        </tfoot>
      )}
    </table>
  );

  return (
    <>
      <div className="ai-chat-table-container">
        <div className="table-export-wrapper">
          <span>Data Table</span>
          <div className="table-actions-group">
            <TableExportButton tableMarkdown={tableMarkdown} hotelName={hotelName} />
            <button
              className="table-expand-btn"
              onClick={() => setIsFullscreen(true)}
              title="Expand table"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        </div>
        <div className="ai-chat-table-wrapper">
          {renderTable()}
        </div>
      </div>

      {isFullscreen && createPortal(
        <div
          className={`ai-table-fullscreen-overlay ${isDarkMode ? 'dark-mode' : ''}`}
          onTouchStart={handleOverlayTouchStart}
          onClick={handleOverlayClick}
        >
          <div className="ai-table-fullscreen-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-table-fullscreen-header">
              <span>Data Table</span>
              <div className="table-actions-group">
                <TableExportButton tableMarkdown={tableMarkdown} hotelName={hotelName} />
                <button
                  className="ai-table-close-btn"
                  onClick={() => setIsFullscreen(false)}
                  title="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="ai-table-fullscreen-body">
              <div className="ai-chat-table-wrapper ai-chat-table-wrapper-fullscreen">
                {renderTable()}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ExpandableAIChatTable;
